/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {
  CountryCode,
  getExampleNumber,
  parsePhoneNumberWithError
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

export type PhoneValidatorOptions = {
  country?: CountryCode;
  required?: boolean;
  allowInternational?: boolean;
};

/**
 * Custom phone number validator using libphonenumber-js
 * @param options Configuration options for validation
 * @returns ValidatorFn for Angular reactive forms
 */
export function phoneNumberValidator(
  options: PhoneValidatorOptions = {}
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value || value.trim() === '') {
      return options.required ? { phoneRequired: true } : null;
    }

    try {
      const phoneNumber = parsePhoneNumberWithError(value, options.country);

      if (!phoneNumber) {
        return { phoneInvalid: true };
      }

      if (!phoneNumber.isValid()) {
        return { phoneInvalid: true };
      }

      // Check country restrictions
      if (options.country && !options.allowInternational) {
        if (phoneNumber.country !== options.country) {
          return {
            phoneWrongCountry: {
              expected: options.country,
              actual: phoneNumber.country
            }
          };
        }
      }

      return null;
    } catch (error) {
      return { phoneInvalid: true };
    }
  };
}

export function validatePhoneNumber(
  event: KeyboardEvent,
  countryCode?: CountryCode
): void {
  const allowedKeys = [
    'Backspace',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
    'Tab',
    '+'
  ];

  const isNumber = /^[0-9]$/.test(event.key);
  if (!isNumber && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }

  const limit = getPhoneLengthLimits(countryCode);

  if (
    limit.maxLength <= (event.target as HTMLInputElement).value.length &&
    !allowedKeys.includes(event.key)
  ) {
    event.preventDefault();
  }
}

export function enforceDigitsOnly(event: KeyboardEvent): void {
  const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

  // allow common shortcuts (copy/paste/select all) so pasting OTP works
  if (event.ctrlKey || event.metaKey) return;

  if (allowedKeys.includes(event.key)) return;
  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
  }
}

export function getPhoneLengthLimits(countryCode?: CountryCode): {
  minLength: number;
  maxLength: number;
} {
  const fallback = { minLength: 6, maxLength: 15 };
  if (!countryCode) {
    return fallback;
  }

  try {
    const example = getExampleNumber(countryCode, examples);
    if (example) {
      const nationalNumber = example.nationalNumber;
      return {
        minLength: nationalNumber.length,
        maxLength: nationalNumber.length
      };
    }
    return fallback;
  } catch (error) {
    console.warn(`Could not get example for country ${countryCode}:`, error);
    return fallback;
  }
}

export const matchFieldsValidator = (
  sourceKey: string,
  confirmKey: string,
  errorKey = 'passwordsMismatch'
): ValidatorFn => {
  return (group: AbstractControl): ValidationErrors | null => {
    const source = group.get(sourceKey);
    const confirm = group.get(confirmKey);
    if (!source || !confirm) return null;

    const s = source.value ?? '';
    const c = confirm.value ?? '';

    // only validate when both have values
    if (!s || !c) {
      removeControlError(confirm, errorKey);
      return null;
    }

    if (s !== c) {
      addControlError(confirm, errorKey);
      return { [errorKey]: true };
    }

    removeControlError(confirm, errorKey);
    return null;
  };
};

function addControlError(ctrl: AbstractControl, key: string): void {
  const prev = ctrl.errors || {};
  if (!prev[key]) ctrl.setErrors({ ...prev, [key]: true });
}

function removeControlError(ctrl: AbstractControl, key: string): void {
  const prev = ctrl.errors || {};
  if (prev[key]) {
    const { [key]: _, ...rest } = prev;
    ctrl.setErrors(Object.keys(rest).length ? rest : null);
  }
}

export const strongPasswordValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value as string;

  if (!value) return null;

  const errors: ValidationErrors = {};

  if (value.length < 8) {
    errors.minlength = true;
  }
  if (!/[A-Z]/.test(value)) {
    errors.uppercase = true;
  }
  if (!/[a-z]/.test(value)) {
    errors.lowercase = true;
  }
  if (!/[0-9]/.test(value)) {
    errors.digit = true;
  }
  if (!/[^A-Za-z0-9]/.test(value)) {
    errors.specialChar = true;
  }

  return Object.keys(errors).length ? errors : null;
};

export function coordinateValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const value = control.value.trim();
    if (!/^-?\d+\.?\d*$/.test(value)) {
      return { invalidFormat: true };
    }
    const num = parseFloat(value);
    if (isNaN(num) || num < min || num > max) {
      return { outOfRange: true };
    }
    return null;
  };
}

export function applyFormViolations(
  form: FormGroup,
  error: HttpErrorResponse
): void {
  const violations = error.error?.violations;

  if (violations && Array.isArray(violations)) {
    violations.forEach(violation => {
      const control: AbstractControl | null = form.get(violation.propertyPath);

      if (control) {
        control.setErrors({ httpError: violation.title });
        control.markAsTouched();
      }
    });
  }
}

export function noWhiteSpaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length === 0) {
      return { noWhiteSpace: true };
    }

    return null;
  };
}
